import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Moon, Sun } from 'lucide-react';
import { cx } from '../lib/cx';
import { useTheme } from '../lib/theme';
import { useLogout, useSession } from '../features/auth/useAuth';

const TABS = [
  { to: '/transfers', label: 'Transfers' },
  { to: '/clients', label: 'Clients' },
  { to: '/admin-users', label: 'Admin Users' },
] as const;

function AccountMenu({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { effective, toggle } = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  async function signOut() {
    await logout.mutateAsync().catch(() => undefined);
    navigate('/login', { replace: true });
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1 rounded px-2 py-1 text-[13px] text-ink-muted hover:bg-surface-sunken hover:text-ink"
      >
        {email}
        <ChevronDown size={13} aria-hidden="true" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-52 rounded border border-hairline-strong bg-surface-raised py-1 shadow-popover"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              toggle();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-ink hover:bg-surface-sunken"
          >
            {effective === 'dark' ? (
              <Sun size={14} aria-hidden="true" />
            ) : (
              <Moon size={14} aria-hidden="true" />
            )}
            {effective === 'dark' ? 'Light theme' : 'Dark theme'}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-ink hover:bg-surface-sunken"
          >
            <LogOut size={14} aria-hidden="true" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function AppLayout() {
  const session = useSession();

  return (
    // A viewport-height column: the header takes what it needs, `main` takes the rest.
    // Measured rather than calculated, so a 1px border on the header cannot leave the
    // page one pixel taller than the screen and hand it a scrollbar of its own.
    <div className="flex h-screen flex-col bg-canvas">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className="z-20 shrink-0 border-b border-hairline bg-surface">
        <div className="mx-auto flex h-12 max-w-[1400px] items-center gap-6 px-4">
          <span className="whitespace-nowrap text-[13px] font-semibold text-ink">
            BAEX Transfer File Service — Admin
          </span>
          <nav aria-label="Main" className="flex flex-1 items-center gap-1">
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) =>
                  cx(
                    'rounded px-2.5 py-1 text-[13px]',
                    isActive
                      ? 'bg-primary-soft font-medium text-primary'
                      : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                  )
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
          <AccountMenu email={session.data?.email ?? ''} />
        </div>
      </header>
      {/*
        `tabIndex={-1}` is what makes the skip link actually skip. Without it the browser
        scrolls to `#main-content` and leaves keyboard focus on the link, so the very next
        Tab goes back into the navigation the reader just asked to jump over.

        Fixed height plus a flex column, so each screen has exactly one scrolling region —
        its own table — instead of a page scrollbar wrapped around a table scrollbar. With
        two of them, scrolling the outer one moves nothing and never reaches the bottom
        sentinel that loads the next fifty rows. `overflow-y-auto` is the safety valve for
        a viewport too short for the screen's fixed furniture.
      */}
      <main
        id="main-content"
        tabIndex={-1}
        className="mx-auto flex w-full min-h-0 flex-1 max-w-[1400px] flex-col overflow-y-auto px-4 py-5 focus:outline-none"
      >
        <Outlet />
      </main>
    </div>
  );
}
